import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Zap, FileText, Eye, Edit } from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import HierarchyCard from "../../../components/HierarchyCard";
import { Button } from "@shared/ui/button";
import { UpdatePlanModal, SamplePlan } from "./UpdatePlanModal";
import { PageHeader } from "@shared/components/PageHeader";

export const UnitDetail = ({ data }: { data: any }) => {
  const navigate = useNavigate();
  const basicInfo = data?.basicInfo || data;
  const unitName = basicInfo?.name || "N/A";

  const currentPlan = data?.planInfo;

  // Active Plan State
  const [activePlan, setActivePlan] = useState<{
    id?: string | number;
    name: string;
    shortName: string;
    utilities: string[];
  }>({
    id: currentPlan?.id,
    name: currentPlan?.planName || "N/A",
    shortName: currentPlan?.shortName || "",
    utilities: (() => {
      const raw = currentPlan?.utilityService;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string" && raw) return [raw];
      return [];
    })(),
  });

  useEffect(() => {
    if (currentPlan) {
      setActivePlan({
        id: currentPlan.id,
        name: currentPlan.planName || "N/A",
        shortName: currentPlan.shortName || "",
        utilities: (() => {
          const raw = currentPlan.utilityService;
          if (Array.isArray(raw)) return raw;
          if (typeof raw === "string" && raw) return [raw];
          return [];
        })(),
      });
    }
  }, [currentPlan]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);

  const unitUtilityServices: string[] = (() => {
    const raw = basicInfo?.utilityServices;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw) return [raw];
    return [];
  })();

  const handleConfirmPlanUpdate = (selectedPlan: SamplePlan) => {
    setActivePlan({
      id: selectedPlan.id,
      name: selectedPlan.name,
      shortName: selectedPlan.shortName,
      utilities: selectedPlan.utilities,
    });
    setIsModalOpen(false);
  };

  const locationDetails = [
    {
      label: "location type",
      value: (
        <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg inline-block">
          {basicInfo?.locationType || "N/A"}
        </span>
      ),
    },
    {
      label: "occupancy status",
      value: (
        <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg inline-block">
          {basicInfo?.occupancyStatus || "N/A"}
        </span>
      ),
    },
    {
      label: "ownership type",
      value: (
        <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg inline-block">
          {basicInfo?.ownershipType || "N/A"}
        </span>
      ),
    },
    {
      label: "latitude",
      value: (
        <p className="text-sm font-semibold text-gray-800">
          {basicInfo?.latitude || "N/A"}
        </p>
      ),
    },
    {
      label: "longitude",
      className: "col-span-2 sm:col-span-1",
      value: (
        <p className="text-sm font-semibold text-gray-800">
          {basicInfo?.longitude || "N/A"}
        </p>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <PageHeader
          title="Unit Details"
          subtitle="View and manage unit location, occupancy, and plan configurations."
        />
        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-2xs space-y-4">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            Location & Occupancy
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-1">
            {locationDetails.map((detail, index) => (
              <ValueLabel
                key={index}
                label={detail.label}
                value={detail.value}
                className={detail.className}
              />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-2xs space-y-4">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <Zap className="w-4 h-4 text-gray-500" />
            Utility Services ({unitUtilityServices.length})
          </h3>

          <div className="flex flex-wrap items-center gap-2.5 pt-1">
            {unitUtilityServices.length > 0 ? (
              unitUtilityServices.map((service: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100/80 rounded-full inline-flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                  {service}
                </span>
              ))
            ) : (
              <span className="text-xs font-medium text-gray-500">N/A</span>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200/80 p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Attached Plan
            </h3>
            <div className="flex items-center gap-2">
              <Button onClick={() => setIsModalOpen(true)}>
                <Edit className="w-3.5 h-3.5" />
                Update Plan
              </Button>
              <Button
                onClick={() => {
                  if (activePlan.id) {
                    navigate(
                      `/plan-tarrif/plan/view/${activePlan.id}?tab=details`,
                    );
                  }
                }}
                variant="outline"
              >
                <Eye className="w-3.5 h-3.5" />
                View Details
              </Button>
            </div>
          </div>

          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/40 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-gray-900">
                {activePlan.name}
              </h4>
              {activePlan.shortName && (
                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded border border-gray-200/60">
                  {activePlan.shortName}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {activePlan.utilities.length > 0 ? (
                activePlan.utilities.map((service: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-600 border border-blue-100/80 rounded-full inline-flex items-center gap-1.5"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    {service}
                  </span>
                ))
              ) : (
                <span className="text-xs font-medium text-gray-500">N/A</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <HierarchyCard currentLevelKey="unit" currentEntityName={unitName} />
      </div>

      <UpdatePlanModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        activePlan={activePlan}
        unitId={basicInfo?.code}
        onConfirmUpdate={handleConfirmPlanUpdate}
      />
    </div>
  );
};

export default UnitDetail;
